import { makeResetStyles, makeStyles, mergeClasses } from '@fluentui/react-components';
import { tokens, typographyStyles } from '@fluentui-copilot/tokens';
import type { ReferenceSlots, ReferenceState } from './Reference.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const referenceClassNames: SlotClassNames<ReferenceSlots> = {
  root: 'fai-Reference',
  citation: 'fai-Reference__citation',
  divider: 'fai-Reference__divider',
  graphic: 'fai-Reference__graphic',
  content: 'fai-Reference__content',
};

export const referenceExtraClassNames = {
  graphicChild: 'fai-Reference__graphicChild',
};

const GRAPHIC_SIZE = '16px';

const useRootBaseClassName = makeResetStyles({
  alignItems: 'center',
  backgroundColor: tokens.colorNeutralBackground3,
  border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  borderRadius: tokens.borderRadiusMedium,
  boxSizing: 'border-box',
  color: tokens.colorNeutralForeground2,
  columnGap: tokens.spacingHorizontalXXS,
  display: 'inline-flex',
  flexShrink: 0,
  justifyContent: 'center',
  maxWidth: '100%',
  minHeight: '24px',
  padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
  textDecoration: 'none',

  ':hover': {
    backgroundColor: tokens.colorNeutralBackground3Hover,
    borderColor: tokens.colorNeutralStroke1Hover,
    color: tokens.colorNeutralForeground2Hover,

    [`& .${referenceClassNames.divider}`]: {
      backgroundColor: tokens.colorNeutralStroke1Hover,
    },
  },

  ':hover:active': {
    backgroundColor: tokens.colorNeutralBackground3Pressed,
    borderColor: tokens.colorNeutralStroke1Pressed,
    color: tokens.colorNeutralForeground2Pressed,

    [`& .${referenceClassNames.divider}`]: {
      backgroundColor: tokens.colorNeutralStroke1Pressed,
    },
  },
});

export const useRootStyles = makeStyles({
  overflow: {
    maxWidth: '100%',
    width: '100%',
  },
});

const useRootNextStyles = makeStyles({ root: { borderRadius: tokens.borderRadiusXLarge } });

const useCitationBaseClassName = makeResetStyles({
  ...typographyStyles.caption2Strong,
});

const useDividerBaseClassName = makeResetStyles({
  backgroundColor: tokens.colorNeutralStroke2,
  height: '16px',
  margin: `${tokens.spacingVerticalNone} ${tokens.spacingHorizontalXS}`,
  width: '1px',
});

const useGraphicBaseClassName = makeResetStyles({
  display: 'inline-flex',
  fontSize: GRAPHIC_SIZE,
  height: GRAPHIC_SIZE,
  lineHeight: GRAPHIC_SIZE,
  width: GRAPHIC_SIZE,

  [`> .${referenceExtraClassNames.graphicChild}`]: {
    height: GRAPHIC_SIZE,
    width: GRAPHIC_SIZE,
  },
});

const useContentBaseClassName = makeResetStyles({
  alignItems: 'center',
  columnGap: tokens.spacingHorizontalSNudge,
  display: 'inline-flex',
  textAlign: 'start',
  wordBreak: 'break-word',
  ...typographyStyles.caption1,
});

/**
 * Apply styling to the Reference slots based on the state
 */
export const useReferenceStyles_unstable = (state: ReferenceState): ReferenceState => {
  'use no memo';

  const { designVersion } = state;

  const rootBaseClassName = useRootBaseClassName();
  const rootNextStyles = useRootNextStyles();
  const citationBaseClassName = useCitationBaseClassName();
  const dividerBaseClassName = useDividerBaseClassName();
  const contentBaseClassName = useContentBaseClassName();
  const graphicBaseClassName = useGraphicBaseClassName();

  state.root.className = mergeClasses(
    referenceClassNames.root,
    rootBaseClassName,
    designVersion === 'next' && rootNextStyles.root,
    state.root.className,
  );

  if (state.citation) {
    state.citation.className = mergeClasses(
      referenceClassNames.citation,
      citationBaseClassName,
      state.citation.className,
    );
  }

  if (state.divider) {
    state.divider.className = mergeClasses(referenceClassNames.divider, dividerBaseClassName, state.divider.className);
  }

  if (state.content) {
    state.content.className = mergeClasses(referenceClassNames.content, contentBaseClassName, state.content.className);
  }

  if (state.graphic) {
    state.graphic.className = mergeClasses(referenceClassNames.graphic, graphicBaseClassName, state.graphic.className);
  }

  return state;
};
